import { useTranslation } from 'react-i18next';
import { Droplets, Icon, Wind, type LucideIcon } from '../../ui/Icon';
import { WEATHER_TONE, weatherCondition } from '../../../utils/weatherSymbol';
import type { DayHours, HourWeather } from '../../../api/weather.api';

// The SVG is pure geometry stretched to the panel width: every label lives in
// HTML around it. `preserveAspectRatio="none"` would squash `<text>` and stroke
// widths alike, and `vectorEffect` only rescues the strokes.
const VB_W = 240;
const TEMP_TOP = 6;
const TEMP_BOTTOM = 46;
const RAIN_H = 22;
// The band under the curve exists to hold rain bars. A dry day has none, so it
// gets the short baseline instead of 26 px of empty card.
const BASE_WET = 72;
const BASE_DRY = 52;

// Below this the bars are noise: 0.1 mm drawn against a 0.1 mm maximum fills the
// whole band and reads as a downpour.
const RAIN_SCALE_FLOOR_MM = 2;

const MS_TO_KMH = 3.6;

// teal-400 — SVG paint takes a literal, same as CategoryDonut's CAT_HEX.
const TEAL = '#2dd4bf';

const temps = (hours: HourWeather[]): number[] =>
  hours.map(h => h.temp).filter((t): t is number => t !== null);

const hourOf = (h: HourWeather): number => Number(h.time.slice(11, 13));

/** The condition nearest midday — the same rule the day chip uses. */
function middaySymbol(hours: HourWeather[]): string | null {
  let symbol: string | null = null;
  let closest = Infinity;
  for (const h of hours) {
    const distance = Math.abs(hourOf(h) - 12);
    if (h.symbol && distance < closest) {
      symbol = h.symbol;
      closest = distance;
    }
  }
  return symbol;
}

/**
 * Rain and wind, sitting on the summary's tinted band.
 *
 * Deliberately not pills: the band is already a surface, and a filled chip on
 * top of it is a box inside a box for two numbers that fit in six characters.
 */
function Metric({ icon, className, children }: {
  icon: LucideIcon; className: string; children: React.ReactNode;
}) {
  return (
    <span className={`inline-flex items-center gap-1 font-mono text-[10px] tabular-nums ${className}`}>
      <Icon as={icon} size={11} strokeWidth={2} />
      {children}
    </span>
  );
}

/** The headline: how warm, what it looks like, and what to bring. */
function Summary({ hours }: { hours: HourWeather[] }) {
  const { t } = useTranslation();
  const all = temps(hours);
  const rain = Math.round(hours.reduce((sum, h) => sum + (h.precipMm ?? 0), 0) * 10) / 10;
  const wind = Math.round(Math.max(0, ...hours.map(h => h.windSpeed ?? 0)) * MS_TO_KMH);
  const condition = weatherCondition(middaySymbol(hours));
  const max = all.length > 0 ? Math.round(Math.max(...all)) : null;
  const min = all.length > 0 ? Math.round(Math.min(...all)) : null;
  const tone = WEATHER_TONE[condition?.tone ?? 'cloud'];

  // The trace itself is aria-hidden — an SVG of 24 points reads as nothing —
  // so this line is the whole day for a screen reader. Same key the day chip
  // uses, already translated in all five locales.
  const aria = condition && max !== null
    ? t('itinerary.weather.aria', {
        condition: t(`itinerary.weather.condition.${condition.labelKey}`), max, min,
      })
    : undefined;

  return (
    <div className={`flex items-center gap-3 rounded-2xl px-3 py-2.5 ${tone.band}`} aria-label={aria}>
      {condition && (
        <span className={`shrink-0 ${tone.icon}`}>
          <Icon as={condition.icon} size={28} strokeWidth={1.4} />
        </span>
      )}

      <div className="min-w-0 flex-1">
        {max !== null && (
          <p className="flex items-baseline gap-1.5 leading-none" aria-hidden="true">
            <span className="font-mono text-[26px] font-semibold tabular-nums tracking-tight text-slate-900 dark:text-slate-50">
              {max}°
            </span>
            <span className="font-mono text-sm tabular-nums text-slate-400 dark:text-slate-500">
              {min}°
            </span>
          </p>
        )}
        {condition && (
          <p className="mt-1.5 text-[11px] leading-none text-slate-500 dark:text-slate-400 truncate">
            {t(`itinerary.weather.condition.${condition.labelKey}`)}
          </p>
        )}
      </div>

      <div className="shrink-0 flex flex-col items-end gap-1.5">
        {rain > 0 && (
          <Metric icon={Droplets} className="text-sky-600 dark:text-sky-400">
            {t('itinerary.weather.panel.precip', { mm: rain })}
          </Metric>
        )}
        {wind > 0 && (
          <Metric icon={Wind} className="text-slate-500 dark:text-slate-400">
            {t('itinerary.weather.panel.wind', { kmh: wind })}
          </Metric>
        )}
      </div>
    </div>
  );
}

/**
 * The 24-step day: a temperature line with the rain underneath.
 *
 * Only ever rendered for `granularity: 'hourly'`. Joining points into a curve is
 * a claim about the moments in between, and that claim is only true when the
 * provider actually sampled every hour (ADR-0006).
 */
function HourlyCurve({ hours }: { hours: HourWeather[] }) {
  const all = temps(hours);
  const lo = Math.min(...all);
  const hi = Math.max(...all);
  const span = hi - lo || 1;

  const pct = (i: number) => (i / Math.max(hours.length - 1, 1)) * 100;
  const x = (i: number) => (pct(i) / 100) * VB_W;
  const y = (temp: number) => TEMP_BOTTOM - ((temp - lo) / span) * (TEMP_BOTTOM - TEMP_TOP);

  const points = hours
    .map((h, i) => (h.temp === null ? null : `${x(i).toFixed(1)},${y(h.temp).toFixed(1)}`))
    .filter((p): p is string => p !== null);

  const rainMax = Math.max(...hours.map(h => h.precipMm ?? 0), RAIN_SCALE_FLOOR_MM);
  const hasRain = hours.some(h => (h.precipMm ?? 0) > 0);
  const barW = VB_W / hours.length;
  const base = hasRain ? BASE_WET : BASE_DRY;
  // Rendered at exactly this many CSS pixels, so a viewBox `y` is also a `top`
  // in px for the HTML overlaid on it. The peak marker depends on that 1:1
  // mapping — hence the inline height instead of a class that could drift.
  const height = base + 4;

  // Ticks come from the readings, not from a fixed 00/06/12/18/24 row: the first
  // day of a trip starts at the current hour, and a label under a column that
  // isn't there is a lie about when the curve begins.
  const ticks = hours
    .map((h, i) => ({ hour: hourOf(h), at: pct(i) }))
    .filter(tick => tick.hour % 6 === 0);

  const peak = hours.findIndex(h => h.temp === hi);
  const peakAt = peak < 0 ? null : pct(peak);

  return (
    <div className="mt-3">
      <div className="relative">
        <svg
          viewBox={`0 0 ${VB_W} ${height}`} preserveAspectRatio="none"
          className="w-full" style={{ height }} aria-hidden="true"
        >
          <defs>
            <linearGradient id="trace-fill" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={TEAL} stopOpacity="0.28" />
              <stop offset="100%" stopColor={TEAL} stopOpacity="0" />
            </linearGradient>
          </defs>

          {ticks.map(tick => (
            <line
              key={`grid-${tick.hour}`}
              x1={(tick.at / 100) * VB_W} y1={TEMP_TOP - 4}
              x2={(tick.at / 100) * VB_W} y2={base}
              className="stroke-slate-100 dark:stroke-slate-800"
              strokeWidth={1} vectorEffect="non-scaling-stroke"
            />
          ))}

          {points.length > 1 && (
            <polygon
              points={`0,${TEMP_BOTTOM} ${points.join(' ')} ${VB_W},${TEMP_BOTTOM}`}
              fill="url(#trace-fill)"
            />
          )}

          {hasRain && hours.map((h, i) => {
            const mm = h.precipMm ?? 0;
            if (mm <= 0) return null;
            const barH = Math.max((mm / rainMax) * RAIN_H, 1.5);
            return (
              <rect
                key={h.time}
                x={x(i) - barW / 2} y={base - barH}
                width={barW * 0.66} height={barH} rx={1}
                className="fill-sky-400/70 dark:fill-sky-500/60"
              />
            );
          })}

          <line
            x1={0} y1={base} x2={VB_W} y2={base}
            className="stroke-slate-200 dark:stroke-slate-700"
            strokeWidth={1} vectorEffect="non-scaling-stroke"
          />
          <polyline
            points={points.join(' ')}
            fill="none"
            className="stroke-teal-500 dark:stroke-teal-400"
            strokeWidth={2} strokeLinejoin="round" strokeLinecap="round"
            vectorEffect="non-scaling-stroke"
          />
        </svg>

        {/* The curve's own answer to "when": the day chip already says how warm
            it gets, only the trace can say at what time. */}
        {peakAt !== null && (
          <>
            <span
              className="absolute w-1.5 h-1.5 -translate-x-1/2 -translate-y-1/2 rounded-full bg-teal-500 ring-2 ring-white dark:bg-teal-400 dark:ring-slate-900"
              style={{ left: `${peakAt}%`, top: y(hi) }}
              aria-hidden="true"
            />
            <span
              className="absolute -translate-x-1/2 -translate-y-full font-mono text-[9px] font-semibold tabular-nums text-teal-700 dark:text-teal-300"
              style={{ left: `${Math.min(Math.max(peakAt, 7), 93)}%`, top: y(hi) - 4 }}
              aria-hidden="true"
            >
              {Math.round(hi)}°
            </span>
          </>
        )}
      </div>

      <div className="relative h-3 mt-0.5" aria-hidden="true">
        {ticks.map(tick => (
          <span
            key={tick.hour}
            className="absolute -translate-x-1/2 font-mono text-[9px] tracking-wider text-slate-300 dark:text-slate-600"
            style={{ left: `${Math.min(Math.max(tick.at, 3), 97)}%` }}
          >
            {String(tick.hour).padStart(2, '0')}
          </span>
        ))}
      </div>
    </div>
  );
}

/**
 * The 6-hourly day: four readings, drawn as four separate cards.
 *
 * They are deliberately NOT joined into a line. The provider sampled four
 * moments and knows nothing about the twenty hours between them; a curve here
 * would look exactly like the hourly one and mean something entirely different.
 * The `6 h` badge on the day header is what says so in words.
 */
function SixHourBlocks({ hours }: { hours: HourWeather[] }) {
  const { t } = useTranslation();
  const anyRain = hours.some(h => (h.precipMm ?? 0) > 0);

  return (
    <div className="mt-2.5 grid grid-cols-4 gap-1.5">
      {hours.map(h => {
        const condition = weatherCondition(h.symbol);
        const mm = h.precipMm ?? 0;
        return (
          <div
            key={h.time}
            className="rounded-xl border border-slate-100 dark:border-slate-800 bg-white dark:bg-slate-800/30 py-2 flex flex-col items-center gap-1.5"
          >
            <span className="font-mono text-[9px] tracking-[0.08em] text-slate-400 dark:text-slate-500 leading-none">
              {h.time.slice(11, 13)}h
            </span>
            {condition
              ? <Icon as={condition.icon} size={19} strokeWidth={1.6} className={WEATHER_TONE[condition.tone].icon} />
              : <span className="h-[19px]" />}
            <span className="font-mono text-sm font-semibold tabular-nums text-slate-800 dark:text-slate-100 leading-none">
              {h.temp === null ? '—' : `${Math.round(h.temp)}°`}
            </span>

            {/* The millimetres are only worth the row on a day that has any, and
                then every block keeps the row so the four temperatures stay on
                one baseline. */}
            {anyRain && (
              <span className="font-mono text-[9px] tabular-nums leading-none text-sky-600 dark:text-sky-400">
                {mm > 0 ? t('itinerary.weather.panel.precip', { mm }) : ' '}
              </span>
            )}
          </div>
        );
      })}
    </div>
  );
}

export function DayHoursTrace({ day }: { day: DayHours }) {
  return (
    <>
      <Summary hours={day.hours} />
      {day.granularity === 'hourly'
        ? <HourlyCurve hours={day.hours} />
        : <SixHourBlocks hours={day.hours} />}
    </>
  );
}
